import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { StatCard } from '@/components/StatCard';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Users, Target, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Home() {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      const response = await axios.get(`${API}/metrics`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Failed to load metrics:', error);
      toast.error('Failed to load impact metrics');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-white"
      >
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16 items-center">
            <div>
              <h1 className="text-5xl font-black tracking-tight text-foreground sm:text-6xl lg:text-7xl">
                <span className="text-[#2c5570]">Book Ownership</span> <span className="text-[hsl(var(--accent))]">Changes Everything!</span>
              </h1>
              <div className="mt-6 max-w-[560px]">
                <p className="text-xl leading-relaxed text-[#4a4a4a] mb-6">
                  Imagine growing up without books. No stories to lose yourself in. No mirrors to see yourself reflected. No windows into worlds beyond your own.
                </p>
                <p className="text-base leading-[1.7] text-[#4a4a4a] italic">
                  For 60% of Delaware's children from low-income households, that's reality. The Home Library Project is changing that-delivering 10 curated books each year to every child, building personal home libraries of 100 books by 8th grade.
                </p>
              </div>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link to="/donate">
                  <Button
                    size="lg"
                    className="rounded-[50px] bg-[#fad53a] text-slate-900 shadow-lg hover:bg-[#fcc419] font-bold text-lg"
                    data-testid="hero-donate-button"
                  >
                    Donate Now
                  </Button>
                </Link>
                <a href="#solution">
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-[50px]"
                    data-testid="hero-learn-more-button"
                  >
                    Learn Our Model
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img
                src="/hero-image-2.jpg"
                alt="Children reading with colorful books on shelves - Explore, Create, Play, Imagine, Discover"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Annual Impact by 2032 */}
      <section className="border-b bg-[hsl(var(--secondary))] py-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Desktop: horizontal layout */}
          <div className="hidden lg:flex items-center justify-start gap-3 text-left">
            <div className="flex flex-col whitespace-nowrap">
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-white/70">ANNUAL IMPACT BY 2032</span>
            </div>
            <div className="h-12 w-px bg-[#fad53a]/30" />
            <div className="flex items-center gap-2 whitespace-nowrap border border-white/20 rounded-[20px] px-6 py-5 bg-[#1e3f52]">
              <span className="text-xl font-black text-white">150,000</span>
              <span className="text-xl font-light text-white/70">Kids</span>
            </div>
            <div className="h-12 w-px bg-[#fad53a]/30" />
            <div className="flex items-center gap-2 whitespace-nowrap border border-white/20 rounded-[20px] px-6 py-5 bg-[#1e3f52]">
              <span className="text-xl font-black text-white">1.5 Million</span>
              <span className="text-xl font-light text-white/70">Books</span>
            </div>
            <div className="h-12 w-px bg-[#fad53a]/30" />
            <div className="flex items-center gap-2 whitespace-nowrap border border-white/20 rounded-[20px] px-6 py-5 bg-[#1e3f52]">
              <span className="text-xl font-black text-white">50+</span>
              <span className="text-xl font-light text-white/70">Employees</span>
            </div>
            <div className="h-12 w-px bg-[#fad53a]/30" />
            <div className="flex items-center gap-2 whitespace-nowrap border border-white/20 rounded-[20px] px-6 py-5 bg-[#1e3f52]">
              <span className="text-xl font-black text-white">100%</span>
              <span className="text-xl font-light text-white/70">of Donations to Mission</span>
            </div>
          </div>
          
          {/* Tablet: 2x2 grid */}
          <div className="hidden md:grid lg:hidden grid-cols-2 gap-6 text-center">
            <div className="col-span-2 flex flex-col">
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-white/70 mb-6">ANNUAL IMPACT BY 2032</span>
            </div>
            <div className="flex flex-col gap-0.5 border border-white/20 rounded-[20px] px-6 py-5 bg-[#1e3f52]">
              <span className="text-xl font-black text-white">150,000</span>
              <span className="text-lg font-light text-white/70">Kids</span>
            </div>
            <div className="flex flex-col gap-0.5 border border-white/20 rounded-[20px] px-6 py-5 bg-[#1e3f52]">
              <span className="text-xl font-black text-white">1.5 Million</span>
              <span className="text-lg font-light text-white/70">Books</span>
            </div>
            <div className="flex flex-col gap-0.5 border border-white/20 rounded-[20px] px-6 py-5 bg-[#1e3f52]">
              <span className="text-xl font-black text-white">50+</span>
              <span className="text-lg font-light text-white/70">Employees</span>
            </div>
            <div className="flex flex-col gap-0.5 border border-white/20 rounded-[20px] px-6 py-5 bg-[#1e3f52]">
              <span className="text-xl font-black text-white">100%</span>
              <span className="text-lg font-light text-white/70">of Donations to Mission</span>
            </div>
          </div>
          
          {/* Mobile: stacked layout */}
          <div className="md:hidden flex flex-col gap-6 text-center items-center">
            <div className="flex flex-col">
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-white/70 mb-6">ANNUAL IMPACT BY 2032</span>
            </div>
            <div className="flex flex-col gap-0.5 border border-white/20 rounded-[20px] px-6 py-5 w-64 bg-[#1e3f52]">
              <span className="text-xl font-black text-white">150,000</span>
              <span className="text-lg font-light text-white/70">Kids</span>
            </div>
            <div className="flex flex-col gap-0.5 border border-white/20 rounded-[20px] px-6 py-5 w-64 bg-[#1e3f52]">
              <span className="text-xl font-black text-white">1.5 Million</span>
              <span className="text-lg font-light text-white/70">Books</span>
            </div>
            <div className="flex flex-col gap-0.5 border border-white/20 rounded-[20px] px-6 py-5 w-64 bg-[#1e3f52]">
              <span className="text-xl font-black text-white">50+</span>
              <span className="text-lg font-light text-white/70">Employees</span>
            </div>
            <div className="flex flex-col gap-0.5 border border-white/20 rounded-[20px] px-6 py-5 w-64 bg-[#1e3f52]">
              <span className="text-xl font-black text-white">100%</span>
              <span className="text-lg font-light text-white/70">of Donations to Mission</span>
            </div>
          </div>
        </div>
      </section>

      {/* The Impact of $50 */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-[#f8f9fa] to-[#e9ecef]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold tracking-tight sm:text-5xl mb-6 text-[#2c5570]">
              The Impact of $50
            </h2>
            <p className="text-[20px] leading-[1.7] text-[#4a4a4a] max-w-[800px] mx-auto mb-16">
              Book ownership is the foundation of literacy-and $50 builds that foundation for an entire year. Children who grow up surrounded by books they own develop stronger language skills, higher reading proficiency, and lasting confidence.
              <br /><br />
              Here's exactly how your gift works:
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 mb-12">
            {/* 1 Child for 1 Year */}
            <Card className="transition-all hover:shadow-xl hover:-translate-y-1 duration-200 bg-[#f0f8fa] border-[hsl(var(--accent))]/30">
              <CardContent className="p-8 text-center">
                <div className="mb-6 inline-flex items-center justify-center w-[100px] h-[100px] rounded-full bg-[hsl(var(--accent))]/20">
                  <img src="/icon-kid-new.png" alt="Child" className="h-16 w-16" />
                </div>
                <div className="text-[88px] font-black text-[#14919f] leading-none mb-4">
                  1
                </div>
                <div className="text-sm font-semibold text-[hsl(var(--secondary))] uppercase tracking-wide mb-3">
                  Child for 1 Year
                </div>
                <div className="text-sm text-[#4a4a4a]">
                  Your $50 serves one child's complete annual book set
                </div>
              </CardContent>
            </Card>

            {/* 10 Books per Student */}
            <Card className="transition-all hover:shadow-xl hover:-translate-y-1 duration-200 bg-[#f0f8fa] border-[hsl(var(--accent))]/30">
              <CardContent className="p-8 text-center">
                <div className="mb-6 inline-flex items-center justify-center w-[100px] h-[100px] rounded-full bg-[hsl(var(--accent))]/20">
                  <img src="/icon-books-new.png" alt="Stack of books" className="h-16 w-16" />
                </div>
                <div className="text-[88px] font-black text-[#14919f] leading-none mb-4">
                  10
                </div>
                <div className="text-sm font-semibold text-[hsl(var(--secondary))] uppercase tracking-wide mb-3">
                  Books per Student
                </div>
                <div className="text-sm text-[#4a4a4a]">
                  Carefully curated, delivered directly to their classrooms
                </div>
              </CardContent>
            </Card>

            {/* 100 Books by 8th Grade */}
            <Card className="transition-all hover:shadow-xl hover:-translate-y-1 duration-200 bg-[#f0f8fa] border-[hsl(var(--accent))]/30 sm:col-span-2 lg:col-span-1">
              <CardContent className="p-8 text-center">
                <div className="mb-6 inline-flex items-center justify-center w-[100px] h-[100px] rounded-full bg-[hsl(var(--accent))]/20">
                  <img src="/icon-gradcap-new.png" alt="Graduation cap" className="h-16 w-16" />
                </div>
                <div className="text-[88px] font-black text-[#14919f] leading-none mb-4">
                  100
                </div>
                <div className="text-sm font-semibold text-[hsl(var(--secondary))] uppercase tracking-wide mb-3">
                  Books by 8th Grade
                </div>
                <div className="text-sm text-[#4a4a4a]">
                  A complete home library, owned forever
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Button
              size="lg"
              className="rounded-[50px] bg-[#fad53a] text-slate-900 shadow-lg hover:bg-[#fcc419] font-bold text-lg px-8"
              data-testid="fifty-dollars-donate-button"
            >
              Donate Now
            </Button>
          </div>
        </div>
      </section>

      {/* The Crisis - Delaware Is In A Literacy Crisis */}
      <section 
        className="py-16 sm:py-24 relative"
        style={{
          backgroundImage: 'url(/bg-problem.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
        data-testid="problem-section"
      >
        {/* 85% navy overlay */}
        <div className="absolute inset-0 bg-[#2c5570]/85"></div>
        
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-white/20 text-white text-base backdrop-blur-sm rounded-[50px] uppercase">
              THE PROBLEM
            </Badge>
            <h2 className="text-5xl font-extrabold tracking-tight text-white">
              Delaware Is In A Literacy Crisis
            </h2>
            <p className="mt-6 text-lg text-white/90 max-w-3xl mx-auto leading-relaxed">
              Low literacy isn't just an education problem. It impacts our community and economy. 
              In 2025, fewer than 40% of Delaware's third graders achieved proficiency on the Smarter 
              Balanced English Language Arts assessment. Among students from low-income households, 
              that number dropped below 22%. In parts of Wilmington, fewer than 1 in 10 third graders 
              reached proficiency.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {/* Card 1: Higher Drop Out Rate */}
            <Card className="transition-all hover:shadow-xl hover:-translate-y-1 duration-200 bg-white">
              <CardContent className="p-6 text-center">
                <div className="mb-4 flex justify-center">
                  <img 
                    src="/icon-dropped-out.jpg" 
                    alt="Warning icon" 
                    className="h-20 w-20"
                  />
                </div>
                <h3 className="text-lg font-bold mb-3 text-foreground">
                  High Drop Out Rate
                </h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    Across the United States, students who are not proficient in reading by third grade 
                    are <strong className="text-foreground">4x more likely</strong> to drop out of high school.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Card 2: Few Kids Own Books */}
            <Card className="transition-all hover:shadow-xl hover:-translate-y-1 duration-200 bg-white">
              <CardContent className="p-6 text-center">
                <div className="mb-4 flex justify-center">
                  <img 
                    src="/icon-empty-bookshelf.jpg" 
                    alt="Empty bookshelf icon" 
                    className="h-20 w-20"
                  />
                </div>
                <h3 className="text-lg font-bold mb-3 text-foreground">
                  Few Kids Own Books
                </h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    More than <strong className="text-foreground">60%</strong> of children from low-income households have 
                    no books at home. Children without books start kindergarten with a{' '}
                    <strong className="text-foreground">30-million-word gap</strong>.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Card 3: Huge Cost to Community */}
            <Card className="transition-all hover:shadow-xl hover:-translate-y-1 duration-200 bg-white">
              <CardContent className="p-6 text-center">
                <div className="mb-4 flex justify-center">
                  <img 
                    src="/icon-cost-chart.jpg" 
                    alt="Graph icon" 
                    className="h-20 w-20"
                  />
                </div>
                <h3 className="text-lg font-bold mb-3 text-foreground">
                  Huge Cost to Community
                </h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    Each year, roughly <strong className="text-foreground">1,000 students</strong> drop 
                    out of Delaware schools. The lifetime economic impact per 1,000 dropouts is{' '}
                    <strong className="text-foreground">$272 million</strong>.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section id="solution" className="py-16 sm:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Badge className="bg-[#14919f] text-white text-base backdrop-blur-sm rounded-[50px] uppercase">
                The Solution
              </Badge>
            </div>
            <h2 className="text-4xl font-extrabold tracking-tight sm:text-5xl mb-6 text-[#2c5570]">
              Book Ownership Changes Everything
            </h2>
            <p className="text-[20px] sm:text-[18px] leading-[1.7] text-[#4a4a4a] max-w-[800px] mx-auto">
              Book ownership is the foundation of literacy-and the solution to Delaware's crisis. Children surrounded by books they own read more, score higher, and graduate at dramatically higher rates. Not library books. Not classroom books. <span className="font-bold text-[#14919f]">Their books</span>. We deliver 10 curated titles each year, starting in Pre-K and continuing through 8th grade. By the time they reach high school, they've built a personal library of <span className="font-bold">100 books</span>. Books that shaped them. Books they'll never forget. Books that <span className="font-bold italic">changed everything</span>.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 mb-12">
            <Card className="transition-all hover:shadow-lg duration-200">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="rounded-lg bg-[hsl(var(--accent))]/10 p-2 flex items-center justify-center">
                    <img src="/icon-books-delivery.png" alt="Books" className="h-12 w-12 object-contain" style={{filter: 'contrast(1.2) brightness(0.9)'}} />
                  </div>
                  <h3 className="text-xl font-bold">
                    We deliver curated bags of books directly to children
                  </h3>
                </div>
                <p className="text-base text-[#4a4a4a]">
                  Starting in Pre-K and continuing through 8th grade, each child receives ten high-quality books 
                  every year, building a home library of 100 books by adolescence. Books are delivered through 
                  schools, early learning centers, and community partners, removing every barrier to access.
                </p>
              </CardContent>
            </Card>

            <Card className="transition-all hover:shadow-lg duration-200">
              <CardContent className="p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="rounded-lg bg-[hsl(var(--accent))]/10 p-2 flex items-center justify-center">
                    <img src="/icon-pin-drop.png" alt="Location pin" className="h-12 w-12 object-contain" style={{filter: 'contrast(1.2) brightness(0.9)'}} />
                  </div>
                  <h3 className="text-xl font-bold">
                    We start where the need is greatest
                  </h3>
                </div>
                <p className="text-base text-[#4a4a4a]">
                  Our initial focus is on schools with the lowest literacy rates and highest percentages of 
                  students from low-income households. By reaching these children first, and starting with the 
                  youngest learners, we lay the foundation for lasting, generational impact.
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-gradient-to-br from-[#f1fbfd] to-[#eef6fd] border-[hsl(var(--accent))]/20">
            <CardContent className="p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="rounded-lg bg-[#148a99] p-3 flex items-center justify-center">
                  <img src="/icon-learning-life-white.png" alt="Learning and Life" className="h-12 w-12 object-contain" />
                </div>
                <h3 className="text-xl font-bold">
                  We curate books that connect to learning and life
                </h3>
              </div>
              <p className="text-base text-[#4a4a4a] mb-4">
                Each annual set includes four intentionally selected books aligned with Delaware's educational 
                priorities, alongside six high-interest titles chosen for joy, diversity, and engagement:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-6">
                <div className="flex flex-col items-center text-center gap-3 border-2 border-[#2c5570]/20 rounded-lg p-4">
                  <img src="/icon-financial-lit.png" alt="Financial Literacy" className="h-12 w-12" />
                  <p className="text-base font-bold text-[#148a99]">Financial Literacy</p>
                  <p className="text-sm text-[#666] leading-relaxed">Learning value, saving, and decision-making</p>
                </div>
                <div className="flex flex-col items-center text-center gap-3 border-2 border-[#2c5570]/20 rounded-lg p-4">
                  <img src="/icon-culture.png" alt="Culture" className="h-12 w-12" />
                  <p className="text-base font-bold text-[#148a99]">Diverse Heroes & History</p>
                  <p className="text-sm text-[#666] leading-relaxed">Providing mirrors and windows for kids to see themselves, their communities, and what they can achieve</p>
                </div>
                <div className="flex flex-col items-center text-center gap-3 border-2 border-[#2c5570]/20 rounded-lg p-4">
                  <img src="/icon-stem.png" alt="STEM" className="h-12 w-12" />
                  <p className="text-base font-bold text-[#148a99]">STEM</p>
                  <p className="text-sm text-[#666] leading-relaxed">Discovery, innovation, and problem-solving</p>
                </div>
                <div className="flex flex-col items-center text-center gap-3 border-2 border-[#2c5570]/20 rounded-lg p-4">
                  <img src="/icon-heart-sel.png" alt="SEL" className="h-12 w-12" />
                  <p className="text-base font-bold text-[#148a99]">SEL</p>
                  <p className="text-sm text-[#666] leading-relaxed">Empathy, confidence, and resilience</p>
                </div>
              </div>
              <p className="text-sm text-[#4a4a4a] mt-6">
                Plus 6 high-interest variety books selected for excitement, imagination, and student choice.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Workforce Strategy Teaser */}
      <section className="relative bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))] py-16 sm:py-24 overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{backgroundImage: 'url(/bg-workforce.jpg)'}}
        />
        <div className="absolute inset-0 bg-[#2c5570]/85" />
        
        {/* Content */}
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <Badge className="bg-[#14919f] text-white text-base backdrop-blur-sm rounded-[50px] uppercase">
                Pathways to Purpose
              </Badge>
            </div>
            <h2 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
              We Connect Two Forms of Equity
            </h2>
            <p className="mt-4 text-lg text-[hsl(var(--secondary-foreground))]/90">
              Children gaining access to books. Adults gaining access to opportunity.
            </p>
            <p className="mt-6 text-[18px] leading-[1.7] text-white/90 max-w-[900px] mx-auto">
              The Home Library Project creates two kinds of opportunity: children gaining books, adults gaining careers. We hire young adults, parents, returning citizens, and adults with physical and developmental disabilities-people our community has often overlooked. Every team member earns a living wage, receives paid skills training in literacy and digital competency, and contributes to a mission that transforms lives. This is what equity looks like: meaningful work, dignified employment, and a chance to be part of something bigger.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { title: 'We Hire With Purpose', description: 'Creating meaningful jobs in the communities we serve', icon: '/icon-career.png' },
              { title: 'We See Potential', description: 'Valuing character and commitment over credentials', icon: '/icon-character.png' },
              { title: 'We Build Skills', description: 'Paid time for literacy, numeracy, and digital training', icon: '/icon-skills.png' },
              { title: 'We Grow Together', description: "Strengthening families and Delaware's workforce", icon: '/icon-family.png' },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="mb-3 flex justify-center">
                  <img src={item.icon} alt={item.title} className="h-16 w-16" />
                </div>
                <h3 className="font-semibold text-lg mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-[hsl(var(--secondary-foreground))]/80">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-[#fff9e8] via-[#f4fbfb] to-[#fef6f5] py-16 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <div className="flex justify-center mb-4">
            <Badge className="bg-[#14919f] text-white text-base backdrop-blur-sm rounded-[50px] uppercase">
              DISTRIBUTION BEGINS JANUARY 2027
            </Badge>
          </div>
          <h2 className="text-[40px] sm:text-[56px] font-black tracking-tight leading-tight text-[#2c5570]">
            $50 Changes a Child's Story
          </h2>
          <div className="mt-6">
            <p className="text-[24px] font-bold text-[#2c5570] mb-4">
              Ten books. One year. A lifetime of reading.
            </p>
            <p className="text-[20px] text-[#4a4a4a] leading-[1.65] max-w-[700px] mx-auto">
              Your gift builds a home library that grows with them-from their first picture book in Pre-K to their 100th book by 8th grade.
            </p>
          </div>
          <p className="mt-8 text-[19px] text-[#666] italic leading-[1.6] max-w-[680px] mx-auto mb-14">
            Join us in making Delaware the first state where every child grows up surrounded by books of their own.
          </p>
          <div className="mt-8 flex justify-center">
            <Link to="/donate">
              <Button
                size="lg"
                className="rounded-[50px] bg-[#fad53a] text-slate-900 shadow-lg hover:bg-[#fcc419] font-bold"
                data-testid="cta-donate-button"
              >
                Donate $50
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}